/**
 * Tipos y helpers puros de la sección Integraciones → ClassLink (KB-939).
 *
 * Vive acá y no dentro de los `.vue` porque vitest corre con
 * `environment: 'node'` y sin `@vue/test-utils`: sólo lo que sea una función
 * pura se puede testear en este repo. Los tipos también viven acá (y no en
 * `roster-tenants.service.ts`) para que este archivo no arrastre
 * `backend-v2.service.ts`, que importa `firebase/auth`, `@/store` y `@/router`.
 */

export type RosterSyncRunMode = 'incremental' | 'full';
export type RosterSyncRunTrigger = 'cron' | 'manual';
export type RosterSyncRunStatus = 'running' | 'success' | 'failed';

/** Resultado de una corrida desde el punto de vista de quien la mira, no del proceso. */
export type RunOutcome = 'applied' | 'no_changes' | 'needs_attention';

/**
 * Motivo de un evento, como código más parámetros (KB-946). El backend no manda
 * texto armado: la traducción vive acá, en los archivos de i18n.
 */
export interface SyncEventDetail {
  reason: string;
  params?: Record<string, string | number>;
}

export interface RosterSyncErrorSample {
  stage: string;
  sourcedId: string;
  outcome: string;
  detail?: string | SyncEventDetail | null;
}

export interface RosterSyncSourceTotals {
  orgs: number;
  users: number;
  classes: number;
  enrollments: number;
}

/** `byStage[etapa][outcome] = n`. `sourceTotals` sólo aparece en corridas full. */
export interface RosterSyncSummary {
  byStage: Record<string, Record<string, number>>;
  sourceTotals?: RosterSyncSourceTotals;
}

export interface RosterSyncRun {
  id: number;
  tenantId: number;
  mode: RosterSyncRunMode;
  trigger: RosterSyncRunTrigger;
  triggeredBy: string | null;
  status: RosterSyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  summary: RosterSyncSummary | null;
  errorSample: RosterSyncErrorSample[] | null;
  errorMessage: string | null;
  /** Resultado derivado (KB-946). Opcional: puede faltar si el backend todavía no lo expone, y es null en corridas viejas que el backfill no clasificó. */
  outcome?: RunOutcome | null;
  /** Muestra de eventos omitidos de district/school (KB-946). */
  unresolvedSample?: RosterSyncErrorSample[] | null;
}

/**
 * Registro de BeeReaders que podría corresponderse con una org sin vincular
 * (KB-946). El backend lo busca por nombre exacto, sin filtrar por `active`
 * ni por `districtId`: son justo esos dos filtros los que hacen que el
 * matcher automático no lo encuentre, así que acá tienen que verse para que
 * quien vincula a mano sepa qué está eligiendo.
 */
export interface OrgCandidate {
  internalId: number;
  name: string;
  active: boolean;
  districtId: number | null;
}

/** Org de ClassLink que el sync no pudo mapear a un registro de BeeReaders. */
export interface UnlinkedOrg {
  sourcedId: string;
  name: string;
  /** 'district' | 'school' tal como lo manda ClassLink. */
  type: string;
  parentSourcedId: string | null;
  candidates: OrgCandidate[];
}

export interface ReconciliationResult {
  /** 'live' = se consultó a ClassLink; 'persisted' = no respondió y se degradó
   *  a la última corrida. */
  source: 'live' | 'persisted';
  orgs: UnlinkedOrg[];
}

export interface TenantStats {
  schools: number;
  students: number;
  teachers: number;
  staff: number;
  courses: number;
  mappedUsersCount: number;
}

export interface RosterTenant {
  id: number;
  provider: string;
  tenantKey: string;
  displayName: string;
  endpointUrl: string;
  clientId: string;
  hasSecret: boolean;
  clientSecretHint: string | null;
  enabled: boolean;
  notes: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  stats: TenantStats;
  /** Última corrida de cualquier modo y estado: alimenta el chip y el polling. */
  lastRun: RosterSyncRun | null;
  /** Última corrida `full` con `status === 'success'`: única fuente de `sourceTotals`. */
  lastFullRun: RosterSyncRun | null;
  /** Watermark de `roster_sync_state` (ISO) o null. */
  lastSyncedAt: string | null;
  /** Última corrida con problemas, de cualquier modo (KB-946). Opcional por la misma razón que `outcome`. */
  lastAttentionRun?: RosterSyncRun | null;
  /** Cantidad (de una muestra topeada, no el total) de orgs sin vincular
   *  (KB-946). Opcional por la misma razón que `outcome`. */
  unlinkedOrgsCount?: number;
  /**
   * Totales de origen ya acotados al alcance configurado (KB-950). Presente
   * solo si el tenant tiene alcance (escuelas o grados) configurado; ausente
   * o `null` si no tiene alcance, en cuyo caso se usa el total crudo de
   * `lastFullRun.summary.sourceTotals` (ver `effectiveSourceTotals`).
   */
  scopedSourceTotals?: RosterSyncSourceTotals | null;
}

/** Clave i18n + params, para que el `.vue` haga `$t(msg.key, msg.params)`. */
export interface I18nMessage {
  key: string;
  params?: Record<string, string | number>;
}

export interface StageRow {
  stage: string;
  outcome: string;
  count: number;
}

export const POLL_INTERVAL_MS = 5000;
export const RUNS_PAGE_SIZE = 20;

/**
 * Outcome que cuenta como "error" en el resumen de una corrida.
 * `ambiguous` NO cuenta acá (decisión del controlador): un ambiguo no es un
 * error, es algo que necesita que una persona decida, y se cuenta aparte
 * (ver `AMBIGUOUS_OUTCOMES` y `runCountsMessage`).
 */
const ERROR_OUTCOMES = ['error'];

/** Outcome que cuenta como "ambiguo" en `runCountsMessage`. */
const AMBIGUOUS_OUTCOMES = ['ambiguous'];

/** Outcomes que cuentan como "procesado con éxito" para usuarios y cursos. */
const APPLIED_OUTCOMES = ['created', 'linked', 'resolved', 'deactivated'];

/**
 * Orden de presentación de las etapas conocidas; el resto va alfabético al
 * final. Lista cerrada del orquestador del backend (`SyncStage`).
 */
const STAGE_ORDER = ['district', 'school', 'user', 'class', 'enrollment'];

const STATUS_COLORS: Record<RosterSyncRunStatus, string> = {
  running: 'blue',
  success: 'green',
  failed: 'red',
};

export function defaultEndpointUrl(tenantKey: string): string {
  return `https://${tenantKey.trim()}.rosterserver.com`;
}

export function shouldPoll(tenants: RosterTenant[]): boolean {
  return tenants.some((tenant) => tenant.lastRun !== null
    && tenant.lastRun !== undefined
    && tenant.lastRun.status === 'running');
}

export function relativeTime(
  iso: string | null | undefined,
  now: Date = new Date(),
): I18nMessage | null {
  if (!iso) {
    return null;
  }

  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }

  const seconds = Math.max(0, Math.round((now.getTime() - timestamp) / 1000));
  if (seconds < 60) {
    return { key: 'classlink.relative.just_now' };
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return { key: 'classlink.relative.minutes', params: { n: minutes } };
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return { key: 'classlink.relative.hours', params: { n: hours } };
  }

  return { key: 'classlink.relative.days', params: { n: Math.floor(hours / 24) } };
}

function outcomesOf(
  summary: RosterSyncSummary | null | undefined,
  stage: string,
): Record<string, number> {
  const byStage = summary && summary.byStage ? summary.byStage : {};
  return Object.prototype.hasOwnProperty.call(byStage, stage) ? byStage[stage] : {};
}

function countOutcomes(
  summary: RosterSyncSummary | null | undefined,
  stage: string,
  wanted: string[],
): number {
  const outcomes = outcomesOf(summary, stage);
  return wanted.reduce((total, outcome) => total + (outcomes[outcome] || 0), 0);
}

/** Suma `wanted` a través de todas las etapas de `summary.byStage`. */
function outcomeTotalAllStages(
  summary: RosterSyncSummary | null | undefined,
  wanted: string[],
): number {
  const byStage = summary && summary.byStage ? summary.byStage : {};
  return Object.keys(byStage).reduce((total, stage) => {
    const outcomes = byStage[stage];
    return total + wanted.reduce((acc, outcome) => acc + (outcomes[outcome] || 0), 0);
  }, 0);
}

function errorTotal(summary: RosterSyncSummary | null | undefined): number {
  return outcomeTotalAllStages(summary, ERROR_OUTCOMES);
}

/**
 * Total de omitidos de una corrida, sumado en todas las etapas. Es el número
 * real (no una muestra): lo usan tanto la fila (`runCountsMessage`) como el
 * título del drawer de omitidos, para que los dos no puedan mostrar cifras
 * distintas.
 */
export function skippedTotal(summary: RosterSyncSummary | null | undefined): number {
  return outcomeTotalAllStages(summary, ['skipped']);
}

/**
 * "X usuarios, Y cursos, Z omitidos, W ambiguos, V errores" a partir de
 * `summary.byStage`.
 * `users` = `byStage.user` en `created + linked + resolved + deactivated`.
 * `courses` = `byStage.class` en los mismos outcomes.
 * `skipped` = outcome `skipped` en todas las etapas.
 * `ambiguous` = outcome `ambiguous` en todas las etapas: un ambiguo no es un
 * error, es algo que necesita que una persona decida, así que se cuenta aparte
 * y no adentro de `errors` (ver comentario de `ERROR_OUTCOMES`).
 * `errors` = outcome `error` en todas las etapas.
 */
export function runCountsMessage(run: RosterSyncRun | null | undefined): I18nMessage | null {
  if (!run || !run.summary) return null;

  return {
    key: 'classlink.run.counts',
    params: {
      users: countOutcomes(run.summary, 'user', APPLIED_OUTCOMES),
      courses: countOutcomes(run.summary, 'class', APPLIED_OUTCOMES),
      skipped: skippedTotal(run.summary),
      ambiguous: outcomeTotalAllStages(run.summary, AMBIGUOUS_OUTCOMES),
      errors: errorTotal(run.summary),
    },
  };
}

/** Lo que ClassLink dice que tiene, para comparar contra lo que hay en BeeReaders. */
export interface SourceTotals {
  /** Cuenta también al distrito, no sólo a los colegios: por eso no se llama `schools`. */
  orgs: number;
  users: number;
}

/**
 * Los totales que reportó el Roster Server, que la pantalla muestra al lado de
 * los números propios de cada columna.
 *
 * Se le pasa `tenant.lastFullRun` (última corrida `full` exitosa), no `lastRun`:
 * `sourceTotals` sólo existe en corridas completas, y así la comparación no
 * desaparece porque después haya corrido una incremental.
 *
 * Devuelve los números crudos y no un `I18nMessage` porque cada uno va a una
 * columna distinta: `orgs` junto a Colegios y `users` junto a Estudiantes. Un
 * único mensaje armado acá obligaría a la vista a desarmarlo.
 */
export function sourceTotals(run: RosterSyncRun | null | undefined): SourceTotals | null {
  const totals = run && run.summary ? run.summary.sourceTotals : undefined;
  if (!totals) {
    return null;
  }

  return { orgs: totals.orgs, users: totals.users };
}

/**
 * El total contra el que corresponde comparar: el acotado al alcance si el
 * tenant tiene uno configurado, o el crudo del último full run si no. Punto
 * único de esta regla para que la columna "En ClassLink" y el mismatch no
 * puedan mostrar números distintos (mismo motivo que documenta `skippedTotal`).
 */
export function effectiveSourceTotals(tenant: RosterTenant): SourceTotals | null {
  if (tenant.scopedSourceTotals) {
    return { orgs: tenant.scopedSourceTotals.orgs, users: tenant.scopedSourceTotals.users };
  }
  return sourceTotals(tenant.lastFullRun);
}

/**
 * Una conexión habilitada donde ClassLink dice tener usuarios y BeeReaders no
 * tiene ni un estudiante está rota, sin ambigüedad posible. Los dos números ya
 * estaban en pantalla (la columna y el tooltip) y nada los cruzaba.
 */
export function tenantMismatch(tenant: RosterTenant): I18nMessage | null {
  if (!tenant.enabled) return null;
  const totals = effectiveSourceTotals(tenant);
  if (!totals || totals.users <= 0) return null;
  if (tenant.stats.students > 0) return null;

  return { key: 'classlink.mismatch', params: { users: totals.users } };
}

/**
 * Fecha y hora en el huso horario local de quien mira la pantalla.
 *
 * A diferencia del filtro global `date` (`src/filters/date.ts` →
 * `src/utils/date.utils.ts`), que formatea en UTC a propósito para no correr
 * fechas-sin-hora (ver el comentario de `normalizeDateToYYYYMMDD` ahí), acá sí
 * importa el instante real: el historial de corridas de ClassLink muestra la
 * hora en la que corrió cada sincronización, y en UTC se ve corrida varias
 * horas respecto de la hora local de quien la disparó.
 *
 * No usamos `dateStyle`/`timeStyle` (ECMA-402 v3): el `lib.es5.d.ts` de la
 * TypeScript ~4.1.5 de este repo no tipa esas dos opciones en
 * `Intl.DateTimeFormatOptions` (mismo problema de `useGrouping` documentado
 * en `formatCount` más abajo), así que se arma con las opciones numéricas
 * clásicas, que sí están tipadas.
 *
 * `timeZone` es sólo para tests (determinismo sin depender del huso del
 * runner); sin pasarlo, usa el huso del navegador.
 */
export function formatLocalDateTime(
  iso: string | null | undefined,
  locale: 'es' | 'en' = 'es',
  timeZone?: string,
): string {
  // Mismo guard que `relativeTime`: `new Date(null)` es la época (1969/1970),
  // no NaN, así que sin esto una corrida sin `startedAt` mostraría esa fecha.
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function stageRows(summary: RosterSyncSummary | null | undefined): StageRow[] {
  const byStage = summary && summary.byStage ? summary.byStage : {};
  const stages = Object.keys(byStage).sort((a, b) => {
    const indexA = STAGE_ORDER.indexOf(a);
    const indexB = STAGE_ORDER.indexOf(b);
    if (indexA === indexB) {
      return a.localeCompare(b);
    }
    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }
    return indexA - indexB;
  });

  return stages.reduce((rows: StageRow[], stage) => {
    const outcomes = byStage[stage];
    const stageRowsForStage = Object.keys(outcomes)
      .sort()
      .map((outcome) => ({ stage, outcome, count: outcomes[outcome] }));
    return rows.concat(stageRowsForStage);
  }, []);
}

export function stageLabelKey(stage: string): string {
  return `classlink.stages.${stage}`;
}

export function outcomeLabelKey(outcome: string): string {
  return `classlink.outcomes.${outcome}`;
}

export function runStatusColor(status: RosterSyncRunStatus): string {
  return STATUS_COLORS[status] || 'grey';
}

export function runDurationSeconds(run: RosterSyncRun): number | null {
  if (!run.finishedAt) {
    return null;
  }
  const started = new Date(run.startedAt).getTime();
  const finished = new Date(run.finishedAt).getTime();
  if (Number.isNaN(started) || Number.isNaN(finished)) {
    return null;
  }
  return Math.max(0, Math.round((finished - started) / 1000));
}

/**
 * No usamos `Intl.NumberFormat` acá a propósito.
 *
 * 1) Bajo el TypeScript ~4.1.5 de este repo, `lib.es5.d.ts` tipa
 *    `Intl.NumberFormatOptions.useGrouping` como `boolean`, así que la opción
 *    v3 de ECMA-402 `useGrouping: 'always'` (necesaria para el punto 2) no
 *    compila: `tsc --noEmit` tira TS2322 aunque ESLint y vitest no lo
 *    detecten, y `pnpm build` (type-check de vue-cli) falla.
 * 2) El `es` de CLDR trae `minimumGroupingDigits: 2`, que deja sin agrupar
 *    los números de exactamente 4 dígitos:
 *    `Intl.NumberFormat('es').format(4843)` === `"4843"`, no `"4.843"`.
 * 3) El `browserslist` del proyecto (`> 1%, last 2 versions, not dead`) no
 *    garantiza que todos los navegadores objetivo soporten esa opción v3.
 *
 * Agrupamos a mano con separador fijo por locale (`.` para `es`, `,` para el
 * resto), que es determinístico y no depende de tipos ni de runtime ICU.
 */
export function formatCount(value: number, locale = 'es'): string {
  if (!Number.isFinite(value)) {
    return '0';
  }

  const separator = locale === 'es' ? '.' : ',';
  return String(Math.trunc(value)).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

export function apiStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

/** Muestra el mensaje del backend (409/422 traen texto útil) o el fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })
    ?.response?.data?.message;

  if (typeof message === 'string' && message.length > 0) {
    return message;
  }

  if (Array.isArray(message) && message.length > 0) {
    return message.join(', ');
  }

  return fallback;
}

/**
 * `custom-sort` del `v-data-table`: las conexiones habilitadas van siempre
 * primero (para eso está el flag antes que nada), y dentro de cada grupo se
 * respeta la columna que haya elegido ordenar quien mira la pantalla
 * (`displayName`/`tenantKey`, únicas columnas sortable de la tabla).
 */
export function sortTenants(
  tenants: RosterTenant[],
  sortBy: string[],
  sortDesc: boolean[],
): RosterTenant[] {
  const [key] = sortBy;
  const desc = !!sortDesc[0];

  return [...tenants].sort((a, b) => {
    if (a.enabled !== b.enabled) {
      return a.enabled ? -1 : 1;
    }
    if (!key) {
      return 0;
    }
    const result = String(a[key as keyof RosterTenant])
      .localeCompare(String(b[key as keyof RosterTenant]));
    return desc ? -result : result;
  });
}

/**
 * Serializa el motivo de un evento para el texto plano que se copia al
 * portapapeles. No se traduce a propósito: ese texto se pega en un ticket y lo
 * que sirve ahí es el código del motivo, no la frase para pantalla. Lo que no
 * puede pasar nunca es que salga "[object Object]".
 */
export function formatDetail(
  detail: string | SyncEventDetail | null | undefined,
): string {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (!detail.reason) return '';
  const params = detail.params || {};
  const parts = Object.keys(params).map((key) => `${key}=${params[key]}`);
  return parts.length ? `${detail.reason} (${parts.join(', ')})` : detail.reason;
}

/** Texto plano (TSV) para el botón "Copiar" de la muestra de errores. */
export function formatErrorSample(sample: RosterSyncErrorSample[] | null | undefined): string {
  if (!sample || sample.length === 0) {
    return '';
  }

  return sample
    .map((item) => [item.stage, item.sourcedId, item.outcome, formatDetail(item.detail)].join('\t'))
    .join('\n');
}

/**
 * Convierte el motivo de un evento en una clave de i18n con sus parámetros.
 *
 * Acepta las dos formas a propósito: los eventos nuevos traen `{ reason, params }`,
 * y las filas guardadas antes de KB-946 traen un string libre escrito para
 * desarrolladores. Esas filas no se migran, así que se muestran tal cual bajo una
 * clave propia — lo que no puede pasar nunca es que la pantalla imprima
 * "[object Object]".
 */
export function detailMessage(
  detail: string | SyncEventDetail | null | undefined,
): I18nMessage | null {
  if (!detail) {
    return null;
  }

  if (typeof detail === 'string') {
    return { key: 'classlink.reasons.raw', params: { text: detail } };
  }

  if (!detail.reason) {
    return null;
  }

  return {
    key: `classlink.reasons.${detail.reason}`,
    params: detail.params || {},
  };
}

const ATTENTION_OUTCOMES = ['error', 'ambiguous'];

const OUTCOME_COLORS: Record<RunOutcome, string> = {
  applied: 'green',
  no_changes: 'grey',
  needs_attention: 'amber darken-2',
};

/**
 * Resultado de una corrida para mostrar en pantalla.
 *
 * Prefiere el `outcome` que calcula el backend, que es la fuente de verdad. El
 * fallback local NO es paranoia: el backfill de KB-946 dejó en null las corridas
 * viejas cuyo único problema fue que no se aplicó nada, y además esta pantalla
 * puede desplegarse antes que el backend que expone el campo.
 *
 * Mismo criterio que `deriveRunOutcome` del backend, incluido que `deactivated`
 * cuenta como trabajo aplicado y que `notFound` no cuenta para ningún lado (dar
 * de baja algo que nunca estuvo mapeado no es ni trabajo ni problema). Ojo con el
 * nombre camelizado: el backend lo emite como `not_found`, pero el cliente HTTP
 * del admin (`src/services/backend-v2.service.ts`) hace `humps.camelizeKeys` en
 * profundidad sobre la respuesta, así que a esta pantalla llega ya camelizado
 * (`notFound`) — de ahí que la clave de i18n sea `outcomes.notFound` y no
 * `outcomes.not_found`.
 */
export function runOutcome(
  run: RosterSyncRun | null | undefined,
): RunOutcome | null {
  if (!run) return null;
  if (run.outcome) return run.outcome;
  if (run.status === 'running') return null;
  if (run.status === 'failed') return 'needs_attention';

  const byStage = (run.summary && run.summary.byStage) || {};
  let applied = 0;
  let skipped = 0;
  let attention = 0;

  Object.keys(byStage).forEach((stage) => {
    const outcomes = byStage[stage];
    Object.keys(outcomes).forEach((outcome) => {
      const count = outcomes[outcome];
      if (APPLIED_OUTCOMES.indexOf(outcome) !== -1) applied += count;
      else if (outcome === 'skipped') skipped += count;
      else if (ATTENTION_OUTCOMES.indexOf(outcome) !== -1) attention += count;
    });
  });

  if (attention > 0 || (applied === 0 && skipped > 0)) return 'needs_attention';
  if (applied > 0) return 'applied';
  return 'no_changes';
}

export function outcomeColor(outcome: RunOutcome | null): string {
  return outcome ? OUTCOME_COLORS[outcome] : 'grey';
}

/**
 * Color del chip de una corrida: una sola función para que la fila y el
 * drawer se vean siempre igual (antes cada uno decidía el color por su cuenta
 * y "en curso" salía gris en la fila y azul en el drawer).
 *
 * 1) `failed` manda y es rojo: una caída del proceso es más grave que una
 *    corrida exitosa que sólo omitió registros (antes las dos se veían
 *    ámbar, como `needs_attention`).
 * 2) `running` es azul.
 * 3) el resto usa el color del outcome derivado (verde/gris/ámbar).
 */
export function runChipColor(run: RosterSyncRun | null | undefined): string {
  if (!run) return 'grey';
  if (run.status === 'failed') return runStatusColor('failed');
  if (run.status === 'running') return runStatusColor('running');
  return outcomeColor(runOutcome(run));
}

/**
 * Explica por qué el sync no agarró solo a este candidato. Las dos razones son
 * las que el matcher automático filtra: un colegio inactivo o uno sin distrito
 * nunca coinciden, aunque el nombre sea idéntico.
 */
export function candidateLabel(candidate: OrgCandidate): I18nMessage {
  if (!candidate.active) {
    return { key: 'classlink.reconcile.candidate_inactive', params: { name: candidate.name } };
  }
  if (candidate.districtId === null) {
    return { key: 'classlink.reconcile.candidate_orphan', params: { name: candidate.name } };
  }
  return { key: 'classlink.reconcile.candidate_plain', params: { name: candidate.name } };
}

/**
 * Agrupa la muestra de omitidos por motivo. Lo que le sirve a quien mira no es la
 * lista de 2497 ids, es "2497 quedaron afuera y todos por la misma razón".
 */
export function unresolvedGroups(
  run: RosterSyncRun | null | undefined,
): Array<{ reason: string; count: number; sample: RosterSyncErrorSample[] }> {
  const samples = (run && run.unresolvedSample) || [];
  const byReason = new Map<string, RosterSyncErrorSample[]>();

  samples.forEach((sample) => {
    const { detail } = sample;
    let reason = 'raw';
    if (detail && typeof detail !== 'string' && detail.reason) {
      reason = detail.reason;
    }
    const current = byReason.get(reason) || [];
    current.push(sample);
    byReason.set(reason, current);
  });

  return [...byReason.entries()]
    .map(([reason, sample]) => ({ reason, count: sample.length, sample }))
    .sort((a, b) => b.count - a.count);
}

/* ------------------------------------------------------------------ *
 * Alcance de la sincronización: escuelas y grados (KB-950).
 *
 * ClassLink comparte el roster del distrito entero y no deja filtrar de su
 * lado: Arlington ISD trae 25 escuelas cuando lo contratado son 22 y los
 * grados 1 a 4. Estas son las piezas puras de esa pantalla; el drawer
 * (`ClassLinkScopeDrawer.vue`) sólo orquesta.
 * ------------------------------------------------------------------ */

/**
 * Grados que ClassLink puede reportar pero que la app no soporta (K-12).
 * Se excluyen del listado de opciones seleccionables en el drawer de alcance
 * aunque el backend los reporte en `availableGrades`; el cumplimiento real
 * (que nunca se sincronicen) es responsabilidad de backend-v2, documentado
 * como comentario en KB-950, no de este repo.
 */
export const UNSUPPORTED_GRADE_CODES = ['BIRTH', 'PRENATAL', 'IT'];

/** Códigos de grado sin los no soportados (ver `UNSUPPORTED_GRADE_CODES`). */
export function supportedGrades(codes: string[]): string[] {
  return codes.filter((code) => !UNSUPPORTED_GRADE_CODES.includes(code));
}

/**
 * KB-970: cuánta gente YA sincronizada (en nuestra base, no en vivo contra
 * ClassLink) hay detrás de una escuela. `undefined` en `ScopeOrg.synced` =
 * todavía no hay nada sincronizado para esa escuela.
 */
export interface SyncedCounts {
  studentsCount: number;
  teachersCount: number;
  staffCount: number;
}

/**
 * Org de ClassLink como la ve la pantalla de alcance.
 *
 * No reusa `UnlinkedOrg` a propósito: comparte cuatro campos pero el resto es
 * distinto (`selected` acá, `candidates` allá), y modelarlas con un solo tipo
 * haría que una de las dos pantallas mienta sobre lo que recibe.
 */
export interface ScopeOrg {
  sourcedId: string;
  /** Con `source: 'persisted'` esto ES el `sourcedId`: no hay nombres reales. */
  name: string;
  type: 'district' | 'school';
  parentSourcedId: string | null;
  selected: boolean;
  synced?: SyncedCounts;
}

export interface TenantOrgsResult {
  /** 'live' = se consultó a ClassLink; 'persisted' = no respondió. */
  source: 'live' | 'persisted';
  /** true = no hay filtro guardado, hoy entra el distrito entero. */
  syncsAllOrgs: boolean;
  orgs: ScopeOrg[];
}

/** Grado disponible en el roster. `grade` es el ordinal que manda el backend. */
export interface AvailableGrade {
  code: string;
  grade: number;
  /**
   * KB-970: estudiantes ya sincronizados con este grado. `undefined` para los
   * códigos sin traducción 1:1 a un ordinal interno (BIRTH/PRENATAL/IT/PR/PK/
   * KG/PS/UG/OTHER comparten el ordinal 0 — el backend no reparte ese número
   * entre ellos porque no hay forma correcta de saber cuál trajo a quién).
   */
  studentsCount?: number;
}

/**
 * Respuesta del dry-run de alcance (`POST .../scope/preview`).
 *
 * `noop: true` significa que la selección no filtra nada (vacía, o sólo
 * distritos): no se inactiva ni se reactiva NADA. No es lo mismo que "cero
 * registros afectados por casualidad".
 */
export interface ScopeEnforcementCounts {
  users: number;
  courses: number;
  enrollments: number;
}

export interface ScopeEnforcementPreview {
  noop: boolean;
  deactivate: ScopeEnforcementCounts;
  reactivate: ScopeEnforcementCounts;
}

export interface TenantScope {
  orgs: ScopeOrg[];
  orgIds: string[];
  /** Ids de los padres, que el backend deriva solo: nadie tilda el distrito. */
  parentIds: string[];
  grades: string[];
  syncsAllOrgs: boolean;
  syncsAllGrades: boolean;
  availableGrades: AvailableGrade[];
}

/**
 * El distrito aparece en `orgs` pero no se elige: el backend deriva
 * `parentIds` solo. Todo conteo de esta pantalla mira sólo las escuelas, así
 * que "quedan 3 afuera" nunca incluye al distrito.
 */
export function schoolsOf(orgs: ScopeOrg[]): ScopeOrg[] {
  return orgs.filter((org) => org.type === 'school');
}

/**
 * Cuántas de las escuelas de `orgs` están en `orgIds`.
 *
 * Filtra contra `orgs` y no cuenta `orgIds.length` a secas porque una
 * selección guardada puede traer ids que ya no existen en el roster de hoy
 * (una escuela dada de baja en ClassLink), y esos no se ven en pantalla: si
 * contaran, el aviso de confirmación diría que se excluyen escuelas que nadie
 * puede ver.
 */
export function selectedSchoolCount(orgs: ScopeOrg[], orgIds: string[]): number {
  return schoolsOf(orgs).filter((org) => orgIds.indexOf(org.sourcedId) !== -1).length;
}

/**
 * Filtra la lista por texto libre. Son 25 escuelas con nombres parecidos
 * (Thornton, Speer, South Davis), así que busca también por `sourcedId`: con
 * `source: 'persisted'` el `sourcedId` es lo único que hay para buscar.
 */
export function filterScopeOrgs(orgs: ScopeOrg[], search: string): ScopeOrg[] {
  const needle = (search || '').trim().toLowerCase();
  if (!needle) {
    return orgs;
  }
  return orgs.filter((org) => org.name.toLowerCase().indexOf(needle) !== -1
    || org.sourcedId.toLowerCase().indexOf(needle) !== -1);
}

/**
 * Orden de presentación de los grados: por el ordinal `grade` y, a igualdad,
 * por código.
 *
 * El desempate por código no es cosmético: nueve códigos (BIRTH, PRENATAL,
 * IT, PR, PK, KG, PS, UG, OTHER) tienen todos `grade: 0`, así que sin él el
 * orden dependería del orden en que los devolvió la base.
 */
export function sortGrades(grades: AvailableGrade[]): AvailableGrade[] {
  return [...grades].sort((a, b) => {
    if (a.grade !== b.grade) {
      return a.grade - b.grade;
    }
    return a.code.localeCompare(b.code);
  });
}

/**
 * Clave i18n de la etiqueta de un grado.
 *
 * Va prefijada con `g` a propósito: los códigos numéricos de OneRoster (`01`,
 * `12`) como último segmento de una ruta con puntos son ambiguos para el
 * parser de vue-i18n, que los puede leer como índice de array. Con el prefijo
 * la clave es siempre un identificador y la ruta se resuelve igual para `g01`
 * que para `gKG`.
 *
 * La etiqueta NO se deriva del ordinal: los nueve códigos con `grade: 0`
 * necesitan un texto propio cada uno. Quien la consume usa el
 * `$te(key) ? $t(key) : code` del repo, así que un código nuevo se ve crudo en
 * vez de mostrar la clave sin traducir.
 */
export function gradeLabelKey(code: string): string {
  return `classlink.scope.grade_labels.g${code}`;
}

/**
 * Aviso de confirmación antes de guardar, o `null` si no hace falta ninguno.
 *
 * Destructivo es sólo achicar: pasar de "entra todo" o de N escuelas a un
 * número menor deja gente afuera de la próxima sincronización, y ahí se avisa
 * con cuántas escuelas quedan excluidas.
 *
 * Guardar con cero tildadas NO es destructivo aunque el número baje: eso quita
 * el filtro y hace que vuelva a entrar el distrito entero. Es el caso más
 * fácil de confundir, y confirmarlo con un cartel de advertencia le diría a
 * operaciones exactamente lo contrario de lo que va a pasar.
 */
export function scopeConfirmation(params: {
  orgs: ScopeOrg[];
  syncsAllOrgs: boolean;
  currentOrgIds: string[];
  nextOrgIds: string[];
}): I18nMessage | null {
  const {
    orgs, syncsAllOrgs, currentOrgIds, nextOrgIds,
  } = params;

  if (nextOrgIds.length === 0) {
    return null;
  }

  const remaining = selectedSchoolCount(orgs, nextOrgIds);
  const baseline = syncsAllOrgs
    ? schoolsOf(orgs).length
    : selectedSchoolCount(orgs, currentOrgIds);

  const excluded = baseline - remaining;
  if (excluded <= 0) {
    return null;
  }

  return {
    key: 'classlink.scope.confirm_body',
    params: { excluded, remaining },
  };
}

/** Suma de los tres contadores: sirve para saber si hay algo que informar. */
export const countsTotal = (c: ScopeEnforcementCounts | null | undefined): number => (
  c ? c.users + c.courses + c.enrollments : 0
);
